import {
  IconButton,
  Paper,
  Tooltip,
  alpha,
  Stack,
  Typography,
  Link,
  useModal,
  CroppingModal as CroppingModalHU,
} from 'material-hu';
import TrashIcon from 'src/icons/Trash';
import ImageIcon from 'src/icons/Image';
import { useSnackbar } from 'material-hu/notistack';
import { megabytesToBytes } from 'src/utils/files';
import { CloudUpload as CloudUploadIcon } from 'material-hu/icons';
import { useFormContext } from 'react-hook-form';
import { useDropzone } from 'material-hu/dropzone';
import { useTranslation } from './i18n';

type Props = {
  onDelete?: () => void;
  onUpdate?: (cover: File) => void;
  name: string;
  recommendedWidth?: number;
  recommendedHeight?: number;
};

const FormCoverPicture = ({
  onDelete,
  onUpdate,
  name,
  recommendedWidth = 1100,
  recommendedHeight = 300,
}: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { watch, setValue } = useFormContext();
  const coverPicture = watch(name);

  const handleCoverSave = async (coverP: File) => {
    if (coverP.size > megabytesToBytes(5)) {
      enqueueSnackbar(t('FILE_TOO_LARGE'), { variant: 'error' });
      return null;
    }

    if (onUpdate) {
      await onUpdate(coverP);
    }

    return setValue(
      name,
      { file: coverP, url: null },
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const {
    modal: croppingModal,
    showModal: showCroppingModal,
    closeModal,
  } = useModal(({ file }) => (
    <CroppingModalHU
      cancelLabel={t('GENERAL:CANCEL')}
      saveLabel={t('GENERAL:SAVE')}
      onSave={handleCoverSave}
      file={file}
      onClose={closeModal}
      recommendedWidth={recommendedWidth}
      recommendedHeight={recommendedHeight}
      title={t('EDIT_COVERPICTURE')}
    />
  ));

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: files => {
      if (files[0]) {
        showCroppingModal({ file: files[0] });
      }
    },
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
    maxFiles: 1,
  });

  return (
    <Stack
      sx={{
        mx: 'auto',
        width: '100%',
        maxHeight: recommendedHeight,
        maxWidth: recommendedWidth,
      }}
    >
      {croppingModal}
      {coverPicture ? (
        <>
          <Stack
            sx={{
              justifyContent: 'center',
              aspectRatio: `${recommendedWidth}/${recommendedHeight}`,
              '& img': {
                display: 'block',
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              },
            }}
          >
            <img
              src={coverPicture.url || URL.createObjectURL(coverPicture.file)}
              alt=""
            />
          </Stack>
          <Paper sx={{ ml: 'auto', width: 'fit-content', px: 2 }}>
            <Tooltip title={t('REPLACE_IMAGE')}>
              <IconButton
                edge="start"
                onClick={open}
              >
                <input {...getInputProps()} />
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('REMOVE_IMAGE')}>
              <IconButton
                edge="end"
                onClick={
                  onDelete ||
                  (() =>
                    setValue(name, null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    }))
                }
              >
                <TrashIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        </>
      ) : (
        <Stack
          sx={{
            border: 1,
            borderRadius: 1,
            borderColor: 'divider',
            backgroundColor: theme =>
              alpha(theme.palette.action.active, isDragActive ? 0.1 : 0.05),
            borderStyle: 'dashed',
            width: '100%',
            height: '100%',
            aspectRatio: `${recommendedWidth}/${recommendedHeight}`,
          }}
        >
          <Stack
            {...getRootProps()}
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.5,
              },
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon fontSize="large" />
            <Typography>{t('DRAG_COVER_PICTURE')}</Typography>
            <Typography>{t('OR')}</Typography>
            <Typography>
              <Link
                color="primary"
                underline="always"
              >
                {t('SELECT')}
              </Link>
              {` ${t('FROM_YOUR_PC')}`}
            </Typography>
            <Typography sx={{ mt: { xs: 2, md: 5 }, fontWeight: 600 }}>
              {t('RECOMMENDED', {
                width: recommendedWidth,
                height: recommendedHeight,
              })}
            </Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default FormCoverPicture;
